import type { BlockContent } from '@/blocks/domain/types/BlockContent/BlockContent';
import type { BlockContentDto } from '@/blocks/application/mappers/blockMapper';
import type {
  CommentsContent,
  CustomTextContent,
  ScopeOfWorkContent,
  TermsConditionsContent
} from '@/blocks/domain/types/BlockContent/ProseContent';

/** `custom` has its own mapper (`mapCustomContent`): it can carry the section model as well as prose. */
export type ProseKind = 'scope_of_work' | 'comments' | 'terms_conditions' | 'custom_text';

export type ProseContentDto = Extract<BlockContentDto, { kind: ProseKind }>;

export type ProseContentDtoFor<K extends ProseKind> = ProseContentDto & { kind: K };

/**
 * A prose payload as it can arrive from storage: the editor writes snake_case `body_html`, but seeds,
 * fixtures and older imports carry camelCase `bodyHtml`, and either key (or `title`) can be missing.
 */
type StoredProseContentDto = Omit<ProseContentDto, 'title' | 'body_html'> & {
  /** Gets or sets the section title; missing on some stored payloads. */
  title?: string | null;
  /** Gets or sets the body as the editor writes it (snake_case). */
  body_html?: string | null;
  /** Gets or sets the body as seeds and older imports write it (camelCase). */
  bodyHtml?: string | null;
};

/**
 * Maps a stored prose block to editor content. Reads `body_html` first and falls back to `bodyHtml`
 * (LLAMS-686: a Terms block stored camelCase reopened with an empty body), and turns a missing title
 * or body into `''` so the editor never holds `undefined`.
 */
export function mapProseContent(dto: ProseContentDto): ScopeOfWorkContent | CommentsContent | TermsConditionsContent | CustomTextContent {
  const stored = dto as StoredProseContentDto;
  return {
    kind: stored.kind,
    title: stored.title ?? '',
    bodyHtml: stored.body_html ?? stored.bodyHtml ?? '',
    ...(stored.pageBreakBefore === true ? { pageBreakBefore: true } : {})
  } as ScopeOfWorkContent | CommentsContent | TermsConditionsContent | CustomTextContent;
}

export function fromProseContent(content: Extract<BlockContent, { kind: ProseKind }>): ProseContentDto {
  return {
    kind: content.kind,
    title: content.title,
    body_html: content.bodyHtml,
    ...(content.pageBreakBefore === true ? { pageBreakBefore: true } : {})
  };
}
